import { useState, useRef, useEffect } from "react"
import type { CloudConn, AvatarOption } from "@/App"
import { settingsApi, cloudApi, catalogueApi, storageApi, cliApiKeysApi, cliModulesApi, type CatalogueDTO, type StorageDTO, type EnabledModuleDTO } from "@/lib/api"

export type SkuPrefix = { value: string; dbId: number | null }
export type SkuRule = { id: string; avatarId: string; prefixes: SkuPrefix[] }
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Plus, Trash2, Eye, EyeOff, Check, X, HardDrive, Link, ChevronDown, Loader2, AlertCircle, Pencil } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

// ── Types ─────────────────────────────────────────────────────────────────────

export type CategoryRule = { id: string; prefixes: string[]; category: 'urbain' | 'sport' | 'vintage' | 'kids' }
export type TypeRule     = { id: string; prefix: string; opticType: 'optique' | 'solaire' | 'both' }

const CATEGORIES: { id: CategoryRule['category']; label: string }[] = [
  { id: 'urbain',  label: 'Urbain/Classique' },
  { id: 'sport',   label: 'Sport'             },
  { id: 'vintage', label: 'Vintage/Créateur'  },
  { id: 'kids',    label: 'Kids'              },
]

const OPTIC_TYPES: { id: TypeRule['opticType']; label: string }[] = [
  { id: 'optique', label: 'Optique'  },
  { id: 'solaire', label: 'Solaire'  },
  { id: 'both',    label: 'Les deux' },
]

type Provider = 'openai' | 'google' | 'vertex_ai'

const PROVIDERS: { id: Provider; label: string }[] = [
  { id: 'openai',    label: 'OpenAI'    },
  { id: 'google',    label: 'Google'    },
  { id: 'vertex_ai', label: 'Vertex AI' },
]

type ApiUsage = 'vision' | 'generation'

type ApiEntry = {
  id: number
  provider: Provider
  label: string
  key_hint: string
  model_vision: string | null
  model_generation: string | null
  vertex_location: string | null
  is_active: boolean
  usages: ApiUsage[]
}

const USAGES: { id: ApiUsage; label: string }[] = [
  { id: 'vision',     label: 'Vision'     },
  { id: 'generation', label: 'Generation' },
]

function parseUsages(raw: string): ApiUsage[] {
  return (raw ?? '').split(',').filter((v): v is ApiUsage => v === 'vision' || v === 'generation')
}


// ── Sub-components ─────────────────────────────────────────────────────────────

function StorageBar({ usedGb, reservedGb, quotaGb }: { usedGb: number; reservedGb: number; quotaGb: number }) {
  const usedPct     = Math.min(100, (usedGb / quotaGb) * 100)
  const reservedPct = Math.min(100 - usedPct, (reservedGb / quotaGb) * 100)
  const usedColor   = usedPct > 85 ? 'oklch(0.58 0.17 28)' : usedPct > 65 ? 'oklch(0.65 0.15 58)' : 'oklch(0.145 0 0)'
  return (
    <div className="rounded-full overflow-hidden bg-secondary h-2.5 flex">
      <div style={{ width: `${usedPct}%`, background: usedColor, transition: 'width 0.3s' }} />
      <div style={{ width: `${reservedPct}%`, background: 'oklch(0.75 0.08 60 / 0.6)', transition: 'width 0.3s' }} />
    </div>
  )
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{ width: 36, height: 20, borderRadius: 10, border: 'none', background: on ? 'oklch(0.145 0 0)' : 'oklch(0.84 0 0)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0, padding: 0 }}
    >
      <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )
}


// ── API Entry Form ─────────────────────────────────────────────────────────────

type TestState = { status: 'idle' | 'testing' | 'ok' | 'error'; message: string; models: string[] }

type ApiFormProps = {
  initial?: Partial<Pick<ApiEntry, 'provider' | 'label' | 'usages' | 'model_vision' | 'model_generation' | 'vertex_location'>>
  isEdit?: boolean
  onSave: (provider: Provider, label: string, key: string, usages: ApiUsage[], modelVision: string | null, modelGeneration: string | null, vertexLocation: string | null) => void
  onCancel: () => void
}

function ApiForm({ initial, isEdit, onSave, onCancel }: ApiFormProps) {
  const [provider, setProvider]         = useState<Provider>(initial?.provider ?? 'openai')
  const [usages, setUsages]             = useState<ApiUsage[]>(initial?.usages ?? ['vision'])
  const [label, setLabel]               = useState(initial?.label ?? '')
  const [key, setKey]                   = useState('')
  const [showKey, setShowKey]           = useState(false)
  const [fileName, setFileName]         = useState('')
  const [modelVision, setModelVision]   = useState(initial?.model_vision ?? '')
  const [modelGen, setModelGen]         = useState(initial?.model_generation ?? '')
  const [vertexLocation, setVertexLocation] = useState(initial?.vertex_location ?? '')
  const [test, setTest]               = useState<TestState>({ status: isEdit ? 'ok' : 'idle', message: isEdit ? 'Previously validated' : '', models: [] })
  const fileRef = useRef<HTMLInputElement>(null)

  const isVertex = provider === 'vertex_ai'
  const valid = label.trim() && usages.length > 0 && test.status === 'ok' && (isEdit ? true : !!key.trim())

  const toggleUsage = (u: ApiUsage) =>
    setUsages(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u])

  const handleJsonFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = ev => { setKey(ev.target?.result as string ?? ''); setTest({ status: 'idle', message: '', models: [] }) }
    reader.readAsText(file)
  }

  const handleTest = async () => {
    if (!key.trim()) return
    setTest({ status: 'testing', message: '', models: [] })
    try {
      const r = await cliApiKeysApi.test({ provider, key })
      setTest({ status: r.ok ? 'ok' : 'error', message: r.message, models: [] })
    } catch {
      setTest({ status: 'error', message: 'Request failed', models: [] })
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-border bg-secondary/20">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">Label</label>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Google Vision prod"
          className="h-8 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/30" />
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Provider</label>
          <div className="flex gap-2">
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => { setProvider(p.id); setKey(''); setFileName(''); setTest({ status: 'idle', message: '', models: [] }) }}
                className={["px-3 h-7 rounded-lg border text-xs font-medium transition-colors",
                  provider === p.id ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"].join(' ')}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Usage</label>
          <div className="flex gap-2">
            {USAGES.map(u => (
              <button key={u.id} onClick={() => toggleUsage(u.id)}
                className={["px-3 h-7 rounded-lg border text-xs font-medium transition-colors",
                  usages.includes(u.id) ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"].join(' ')}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {isVertex ? (
          <>
            <label className="text-xs font-medium text-muted-foreground">Service Account JSON</label>
            <div className="flex gap-2 items-center">
              <button onClick={() => fileRef.current?.click()}
                className="h-8 px-3 rounded-lg border border-border bg-background text-sm text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors truncate max-w-xs">
                {fileName || 'Choose .json file…'}
              </button>
              {key && <Check className="w-4 h-4 text-green-600 shrink-0" />}
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleJsonFile} />
            </div>
            <p className="text-xs text-muted-foreground/60">GCP Console → IAM → Service Accounts → Keys → Add Key</p>
          </>
        ) : (
          <>
            <label className="text-xs font-medium text-muted-foreground">API Key</label>
            <div className="flex gap-2">
              <input type={showKey ? 'text' : 'password'} value={key} onChange={e => { setKey(e.target.value); setTest({ status: 'idle', message: '', models: [] }) }}
                placeholder={provider === 'google' ? 'AIza...' : 'sk-prod-...'}
                className="flex-1 h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30" />
              <button onClick={() => setShowKey(v => !v)} className="px-2 text-muted-foreground hover:text-foreground transition-colors">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Test panel */}
      <div className="flex flex-col gap-2">
        <Button size="sm" variant="outline" disabled={!key.trim() || test.status === 'testing'} onClick={handleTest} className="w-fit">
          {test.status === 'testing' && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
          Test connection
        </Button>
        {test.status !== 'idle' && (
          <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${test.status === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : test.status === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-secondary'}`}>
            {test.status === 'ok' ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            {test.message}
          </div>
        )}
      </div>

      {/* Per-usage model inputs — visible after test passes */}
      {test.status === 'ok' && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs font-medium text-muted-foreground">Model Vision</label>
              <input
                value={modelVision}
                onChange={e => setModelVision(e.target.value)}
                placeholder={provider === 'openai' ? 'e.g. gpt-4o-mini' : 'e.g. gemini-2.0-flash'}
                className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs font-medium text-muted-foreground">Model Generation</label>
              <input
                value={modelGen}
                onChange={e => setModelGen(e.target.value)}
                placeholder={provider === 'openai' ? 'e.g. gpt-image-1' : 'e.g. imagen-4.0-generate-preview-06-06'}
                className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
            </div>
          </div>
          {isVertex && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Vertex Location <span className="text-muted-foreground/60">(default: global)</span></label>
              <input
                value={vertexLocation}
                onChange={e => setVertexLocation(e.target.value)}
                placeholder="global (Gemini 3.x) · us-central1 (Gemini 2.x)"
                className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button size="sm" disabled={!valid} onClick={() => onSave(provider, label, key, usages, modelVision || null, modelGen || null, vertexLocation || null)}>
          <Check className="w-3.5 h-3.5 mr-1" /> Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  )
}


// ── Category Auto-Assignment ───────────────────────────────────────────────────

function CategoryRulesSection({ rules, onChange }: { rules: CategoryRule[]; onChange: (r: CategoryRule[]) => void }) {
  const [prefixInputs, setPrefixInputs] = useState<Record<string, string>>({})
  const addRule = () => onChange([...rules, { id: `cat-${Date.now()}`, prefixes: [], category: 'urbain' }])
  const removeRule = (id: string) => onChange(rules.filter(r => r.id !== id))
  const updateCategory = (id: string, category: CategoryRule['category']) => onChange(rules.map(r => r.id === id ? { ...r, category } : r))
  const addPrefix = (ruleId: string) => {
    const prefix = (prefixInputs[ruleId] ?? '').trim().toUpperCase()
    if (!prefix) return
    onChange(rules.map(r => r.id === ruleId ? { ...r, prefixes: [...new Set([...r.prefixes, prefix])] } : r))
    setPrefixInputs(p => ({ ...p, [ruleId]: '' }))
  }
  const removePrefix = (ruleId: string, prefix: string) =>
    onChange(rules.map(r => r.id === ruleId ? { ...r, prefixes: r.prefixes.filter(p => p !== prefix) } : r))

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Category Auto-Assignment</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Map SKU prefixes to a shoot category</p>
        </div>
        <Button size="sm" variant="outline" onClick={addRule}><Plus className="w-3.5 h-3.5 mr-1" />Add rule</Button>
      </div>
      {rules.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-xl">No rules yet</div>
      ) : (
        <div className="flex flex-col gap-2">
          {rules.map(rule => (
            <div key={rule.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-card min-h-[40px]">
              {/* Chips + inline input */}
              <div className="flex flex-wrap items-center gap-1 flex-1">
                {rule.prefixes.map(prefix => (
                  <div key={prefix} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-secondary text-xs font-mono font-medium group">
                    {prefix}
                    <button onClick={() => removePrefix(rule.id, prefix)} className="ml-0.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <input
                  value={prefixInputs[rule.id] ?? ''}
                  onChange={e => setPrefixInputs(p => ({ ...p, [rule.id]: e.target.value.toUpperCase() }))}
                  onKeyDown={e => { if (e.key === 'Enter') addPrefix(rule.id) }}
                  placeholder={rule.prefixes.length === 0 ? 'e.g. KJR' : '+prefix'}
                  maxLength={6}
                  className="w-20 h-6 rounded border-0 bg-transparent px-1 text-xs font-mono focus:outline-none placeholder:text-muted-foreground/40"
                />
              </div>
              <span className="text-muted-foreground/40 text-sm shrink-0">→</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center justify-between gap-2 px-3 h-7 rounded-md border border-border text-xs font-medium hover:bg-secondary/50 transition-colors min-w-[140px]">
                    {CATEGORIES.find(c => c.id === rule.category)?.label}
                    <ChevronDown className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuRadioGroup value={rule.category} onValueChange={v => updateCategory(rule.id, v as CategoryRule['category'])}>
                    {CATEGORIES.map(c => <DropdownMenuRadioItem key={c.id} value={c.id} className="text-xs">{c.label}</DropdownMenuRadioItem>)}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <button onClick={() => removeRule(rule.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── Optic Type Auto-Assignment ─────────────────────────────────────────────────

function TypeRulesSection({ rules, onChange }: { rules: TypeRule[]; onChange: (r: TypeRule[]) => void }) {
  const addRule = () => onChange([...rules, { id: `type-${Date.now()}`, prefix: '', opticType: 'optique' }])
  const removeRule = (id: string) => onChange(rules.filter(r => r.id !== id))
  const update = (id: string, patch: Partial<TypeRule>) => onChange(rules.map(r => r.id === id ? { ...r, ...patch } : r))

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Optic Type Auto-Assignment</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Map SKU prefixes to optic type</p>
        </div>
        <Button size="sm" variant="outline" onClick={addRule}><Plus className="w-3.5 h-3.5 mr-1" />Add rule</Button>
      </div>
      {rules.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-xl">No rules yet</div>
      ) : (
        <div className="flex flex-col gap-2">
          {rules.map(rule => (
            <div key={rule.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-card">
              <input
                value={rule.prefix}
                onChange={e => update(rule.id, { prefix: e.target.value.toUpperCase() })}
                placeholder="e.g. KJR"
                maxLength={6}
                className="w-20 h-7 rounded-md border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
              <span className="text-muted-foreground/40 text-sm shrink-0">→</span>
              <div className="flex rounded-md border overflow-hidden">
                {OPTIC_TYPES.map((t, i) => (
                  <button key={t.id} onClick={() => update(rule.id, { opticType: t.id })}
                    className={["px-3 h-7 text-xs font-medium transition-colors", i > 0 ? "border-l" : "", rule.opticType === t.id ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                    {t.label}
                  </button>
                ))}
              </div>
              <button onClick={() => removeRule(rule.id)} className="ml-auto text-muted-foreground hover:text-destructive transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ── SKU Auto-Assignment Rules ──────────────────────────────────────────────────

function SkuRulesSection({ rules, onChange, avatars }: {
  rules: SkuRule[]
  onChange: (r: SkuRule[]) => void
  avatars: AvatarOption[]
}) {
  const [prefixInputs, setPrefixInputs] = useState<Record<string, string>>({})
  const [, _setAddingModel] = useState(false)
  const [modelPicker, setModelPicker] = useState(false)
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null)
  const addBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!modelPicker) return
    const close = () => setModelPicker(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [modelPicker])

  const openModelPicker = () => {
    const rect = addBtnRef.current?.getBoundingClientRect()
    if (!rect) return
    const popW = 220
    const popH = 220
    const top = window.innerHeight - rect.bottom >= popH ? rect.bottom + 6 : rect.top - popH - 6
    const left = Math.min(rect.right - popW, window.innerWidth - popW - 8)
    setPickerPos({ top, left })
    setModelPicker(v => !v)
  }

  const addModel = (avatarId: string) => {
    if (rules.some(r => r.avatarId === avatarId)) return
    // eslint-disable-next-line react-hooks/purity
    onChange([...rules, { id: `rule-${Date.now()}`, avatarId, prefixes: [] }])
    setModelPicker(false)
    _setAddingModel(false)
  }

  const removeModel = (id: string) => {
    const rule = rules.find(r => r.id === id)
    if (rule) {
      rule.prefixes.forEach(p => { if (p.dbId != null) settingsApi.deleteSkuRule(p.dbId).catch(() => {}) })
    }
    onChange(rules.filter(r => r.id !== id))
  }

  const addPrefix = async (ruleId: string) => {
    const value = (prefixInputs[ruleId] ?? '').trim().toUpperCase()
    if (!value) return
    const rule = rules.find(r => r.id === ruleId)
    if (!rule || rule.prefixes.some(p => p.value === value)) return
    setPrefixInputs(p => ({ ...p, [ruleId]: '' }))
    let dbId: number | null = null
    try {
      const res = await settingsApi.createSkuRule(value, rule.avatarId)
      dbId = res.id
    } catch { /* optimistic — still add locally */ }
    onChange(rules.map(r => r.id === ruleId ? { ...r, prefixes: [...r.prefixes, { value, dbId }] } : r))
  }

  const removePrefix = (ruleId: string, value: string) => {
    const rule = rules.find(r => r.id === ruleId)
    const entry = rule?.prefixes.find(p => p.value === value)
    if (entry?.dbId != null) settingsApi.deleteSkuRule(entry.dbId).catch(() => {})
    onChange(rules.map(r => r.id === ruleId ? { ...r, prefixes: r.prefixes.filter(p => p.value !== value) } : r))
  }

  const usedAvatarIds = new Set(rules.map(r => r.avatarId))

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Model Auto-Assignment</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Map SKU prefixes to a model — auto-assigned on upload</p>
        </div>
        <div className="relative" onClick={e => e.stopPropagation()}>
          <Button ref={addBtnRef} size="sm" variant="outline" onClick={openModelPicker} disabled={usedAvatarIds.size >= avatars.length}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Model
          </Button>
          {modelPicker && pickerPos && (
            <div
              className="fixed z-[9999] bg-background border rounded-xl shadow-xl p-2"
              style={{ top: pickerPos.top, left: pickerPos.left }}
              onClick={e => e.stopPropagation()}
            >
              <div className="grid grid-cols-4 gap-1.5">
                {avatars.filter(a => !usedAvatarIds.has(a.id)).map(a => (
                  <button
                    key={a.id}
                    onClick={() => addModel(a.id)}
                    className="w-12 aspect-[2/3] rounded-md overflow-hidden bg-secondary hover:ring-2 hover:ring-foreground/40 transition-all"
                  >
                    <img src={a.img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {rules.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
          No models yet — add one to define prefix rules
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {rules.map(rule => {
          const avatar = avatars.find(a => a.id === rule.avatarId)
          if (!avatar) return null
          return (
            <div key={rule.id} className="rounded-xl border bg-card overflow-hidden" style={{ display: 'grid', gridTemplateColumns: '96px 1fr' }}>
              {/* Portrait GAUCHE */}
              <img src={avatar.img} alt="" className="w-full h-full object-contain bg-secondary/30" style={{ minHeight: 140 }} />

              {/* Contenu DROITE */}
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="text-xs font-medium text-muted-foreground">SKU Prefixes</span>
                  <button onClick={() => removeModel(rule.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1, alignItems: 'flex-start', alignContent: 'flex-start' }}>
                  {rule.prefixes.length === 0 && (
                    <span className="text-[11px] text-muted-foreground/50 italic">No prefixes yet</span>
                  )}
                  {rule.prefixes.map(p => (
                    <div key={p.value} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-secondary text-xs font-mono font-medium group">
                      {p.value}
                      <button onClick={() => removePrefix(rule.id, p.value)} className="ml-0.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={prefixInputs[rule.id] ?? ''}
                    onChange={e => setPrefixInputs(p => ({ ...p, [rule.id]: e.target.value.toUpperCase() }))}
                    onKeyDown={e => { if (e.key === 'Enter') addPrefix(rule.id) }}
                    placeholder="e.g. AEF"
                    className="flex-1 h-7 rounded-lg border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-foreground/20"
                  />
                  <button
                    onClick={() => addPrefix(rule.id)}
                    disabled={!(prefixInputs[rule.id] ?? '').trim()}
                    className="h-7 px-2 rounded-lg border border-border hover:bg-secondary disabled:opacity-40 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── Main Settings Page ─────────────────────────────────────────────────────────

export function CliSettings({ cloud, onCloudChange, skuRules, onSkuRulesChange, avatars }: {
  cloud: CloudConn
  onCloudChange: (c: CloudConn) => void
  skuRules: SkuRule[]
  onSkuRulesChange: (r: SkuRule[]) => void
  avatars: AvatarOption[]
}) {
  const [modules, setModules] = useState<EnabledModuleDTO[]>([])
  const hasVisionModule = modules.some(m => m.module_id === 'vision' && m.enabled)
  const [apis, setApis]       = useState<ApiEntry[]>([])
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [autoTransfer, setAutoTransfer] = useState(false)
  const [categoryRules, setCategoryRules] = useState<CategoryRule[]>([])
  const [typeRules, setTypeRules]         = useState<TypeRule[]>([])
  const [catalogues, setCatalogues] = useState<CatalogueDTO[]>([])
  const hasCatalogue = catalogues.length > 0
  const [storage, setStorage] = useState<StorageDTO | null>(null)

  // Input naming
  const [geminiRename, setGeminiRename] = useState(false)

  // Output naming (catalogue-aware)
  type OutputNaming = 'ref_modele' | 'marque_ref' | 'marque_inv' | 'custom'
  const [outputFormat, setOutputFormat]         = useState<'jpeg' | 'png' | 'webp'>('jpeg')
  const [outputResolution, setOutputResolution] = useState<'1080x1920' | '1440x2560' | '2160x3840'>('1080x1920')
  const [maxInputPx, setMaxInputPx]             = useState<number | null>(null)  // null = default (1024), 0 = original
  const customPropsRef = useRef<Record<string, unknown>>({})
  const saveProps = (patch: Record<string, unknown>) => {
    customPropsRef.current = { ...customPropsRef.current, ...patch }
    settingsApi.update({ custom_properties: customPropsRef.current }).catch(() => {})
  }
  const [outputNaming, setOutputNaming] = useState<OutputNaming>('ref_modele')
  const [namingCustom, setNamingCustom] = useState('{ref_modele}')

  useEffect(() => {
    cliModulesApi.list().then(setModules).catch(() => {})
    catalogueApi.list().then(setCatalogues).catch(() => {})
    storageApi.get().then(setStorage).catch(() => {})
    cliApiKeysApi.list().then(keys => setApis(keys.map(k => ({
      id: k.id, provider: k.provider as Provider, label: k.label, key_hint: k.key_hint,
      model_vision: k.model_vision ?? null, model_generation: k.model_generation ?? null,
      vertex_location: k.vertex_location ?? null, is_active: k.is_active, usages: parseUsages(k.usage ?? 'vision')
    })))).catch(() => {})
    settingsApi.get().then(s => {
      setAutoTransfer(s.cloud_auto_transfer ?? false)
      const cp = (s.custom_properties ?? {}) as Record<string, unknown>
      customPropsRef.current = cp
      if (cp.outputFormat === 'jpeg' || cp.outputFormat === 'png' || cp.outputFormat === 'webp') setOutputFormat(cp.outputFormat)
      if (cp.outputResolution === '1080x1920' || cp.outputResolution === '1440x2560' || cp.outputResolution === '2160x3840') setOutputResolution(cp.outputResolution)
      if (typeof cp.maxInputPx === 'number') setMaxInputPx(cp.maxInputPx)
    }).catch(() => {})
  }, [])

  const addApi = async (provider: Provider, label: string, key: string, usages: ApiUsage[], modelVision: string | null, modelGen: string | null, vertexLocation: string | null) => {
    try {
      const k = await cliApiKeysApi.create({ provider, label, key, usage: usages.join(','), model_vision: modelVision, model_generation: modelGen, vertex_location: vertexLocation })
      setApis(prev => [...prev, { id: k.id, provider: k.provider as Provider, label: k.label, key_hint: k.key_hint, model_vision: k.model_vision ?? null, model_generation: k.model_generation ?? null, vertex_location: k.vertex_location ?? null, is_active: k.is_active, usages: parseUsages(k.usage ?? 'vision') }])
      setEditing(null)
    } catch { /* ignore */ }
  }

  const updateApi = async (id: number, _provider: Provider, label: string, key: string, usages: ApiUsage[], modelVision: string | null, modelGen: string | null, vertexLocation: string | null) => {
    try {
      const k = await cliApiKeysApi.update(id, { label, key: key || undefined, usage: usages.join(','), model_vision: modelVision, model_generation: modelGen, vertex_location: vertexLocation })
      setApis(prev => prev.map(a => a.id === id ? { ...a, label: k.label, key_hint: k.key_hint, model_vision: k.model_vision ?? null, model_generation: k.model_generation ?? null, vertex_location: k.vertex_location ?? null, usages: parseUsages(k.usage ?? 'vision') } : a))
      setEditing(null)
    } catch { /* ignore */ }
  }

  const removeApi = async (id: number) => {
    try { await cliApiKeysApi.remove(id) } catch { /* ignore */ }
    setApis(prev => prev.filter(a => a.id !== id))
  }

  const toggleApi = async (id: number, v: boolean) => {
    try { await cliApiKeysApi.update(id, { is_active: v }) } catch { /* ignore */ }
    setApis(prev => prev.map(a => a.id === id ? { ...a, is_active: v } : a))
  }

  const refreshCloudStatus = () =>
    cloudApi.status().then(cs => onCloudChange({ google: { connected: cs.google_connected, email: cs.google_email }, dropbox: { connected: cs.dropbox_connected, email: cs.dropbox_email } })).catch(() => {})

  // Handle OAuth callback — backend redirects to /?cloud_connected=google|dropbox
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('cloud_connected')
    const error = params.get('cloud_error')
    if (connected || error) {
      window.history.replaceState({}, '', window.location.pathname)
      if (connected) refreshCloudStatus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const _pollOAuthPopup = (popup: Window | null, onConnected: (cs: Awaited<ReturnType<typeof cloudApi.status>>) => boolean) => {
    const iv = setInterval(async () => {
      if (!popup || popup.closed) { clearInterval(iv); refreshCloudStatus(); return }
      const cs = await cloudApi.status().catch(() => null)
      if (cs && onConnected(cs)) { clearInterval(iv); refreshCloudStatus() }
    }, 2000)
    setTimeout(() => clearInterval(iv), 300_000)
  }

  const [cloudConnectError, setCloudConnectError] = useState<string | null>(null)

  const connectGoogle = async () => {
    setCloudConnectError(null)
    try {
      const { url } = await cloudApi.googleAuthUrl()
      const popup = window.open(url, '_blank', 'width=600,height=700')
      _pollOAuthPopup(popup, cs => !!cs.google_connected)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setCloudConnectError(`Google: ${msg}`)
    }
  }
  const disconnectGoogle = () =>
    cloudApi.disconnect('google').then(cs => onCloudChange({ google: { connected: cs.google_connected, email: cs.google_email }, dropbox: { connected: cs.dropbox_connected, email: cs.dropbox_email } })).catch(() => {})
  const connectDropbox = async () => {
    setCloudConnectError(null)
    try {
      const { url } = await cloudApi.dropboxAuthUrl()
      const popup = window.open(url, '_blank', 'width=600,height=700')
      _pollOAuthPopup(popup, cs => !!cs.dropbox_connected)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setCloudConnectError(`Dropbox: ${msg}`)
    }
  }
  const disconnectDropbox = () =>
    cloudApi.disconnect('dropbox').then(cs => onCloudChange({ google: { connected: cs.google_connected, email: cs.google_email }, dropbox: { connected: cs.dropbox_connected, email: cs.dropbox_email } })).catch(() => {})


  const NAV_SECTIONS = [
    { id: 'features', label: 'Feature Access', desc: 'Modules enabled by your admin'   },
    { id: 'api',    label: 'API Keys',      desc: 'Image generation providers'       },
    { id: 'cloud',  label: 'Cloud & Storage', desc: 'Drive, Dropbox & disk usage'     },
    { id: 'output', label: 'Input & Output', desc: 'Naming & format presets'          },
    { id: 'sku',    label: 'Custom Data',   desc: 'Per-SKU property schema'          },
  ]
  const [activeSection, setActiveSection] = useState('api')

  return (
    <div className="flex gap-8 items-start h-full">
      {/* ── Left nav ── */}
      <nav className="w-52 shrink-0 flex flex-col gap-1 pt-0.5">
        {NAV_SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={[
              "text-left px-3 py-2.5 rounded-lg transition-colors group",
              activeSection === s.id ? "bg-secondary" : "hover:bg-secondary/60"
            ].join(' ')}
          >
            <div className={["text-sm font-medium", activeSection === s.id ? "text-foreground" : "text-foreground/70 group-hover:text-foreground"].join(' ')}>{s.label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{s.desc}</div>
          </button>
        ))}
      </nav>

      {/* ── Right content ── */}
      <div className="flex-1 min-w-0">

        {/* API Keys */}
        {activeSection === 'features' && (
          <section className="flex flex-col gap-4">
            <div>
              <h3 className="text-sm font-semibold">Feature Access</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Modules enabled for your account by your administrator</p>
            </div>
            {(() => {
              const active = modules.filter(m => m.enabled)
              if (active.length === 0) return (
                <div className="text-center py-10 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
                  No features enabled yet — contact your administrator
                </div>
              )
              return (
                <div className="flex flex-col gap-4">
                  {active.map(mod => {
                    const isAvatarMod = mod.module_id === 'add_product_on_avatar'
                    return (
                      <div key={mod.module_id} className="rounded-xl border border-border p-4 flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{mod.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-foreground text-background">{mod.category}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{mod.description}</span>
                        {isAvatarMod && avatars.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {avatars.map(a => (
                              <div key={a.id} className="flex flex-col items-center gap-1">
                                <img src={a.img} alt={a.name}
                                  className="w-12 h-16 rounded-lg object-cover border border-border" />
                                <span className="text-[10px] text-muted-foreground text-center max-w-[48px] truncate">{a.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </section>
        )}

        {activeSection === 'api' && (
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold tracking-tight">API Keys</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Providers used for image generation</p>
              </div>
              {editing !== 'new' && (
                <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              )}
            </div>
            {editing === 'new' && <ApiForm onSave={addApi} onCancel={() => setEditing(null)} />}
            <div className="flex flex-col gap-3">
              {apis.map(api => (
                <Card key={api.id} className={`transition-opacity ${api.is_active ? '' : 'opacity-55'}`}>
                  {editing === api.id ? (
                    <div className="p-4">
                      <ApiForm
                        isEdit
                        initial={{ provider: api.provider, label: api.label, usages: api.usages, model_vision: api.model_vision, model_generation: api.model_generation, vertex_location: api.vertex_location }}
                        onSave={(prov, lbl, key, usgs, mv, mg, vl) => updateApi(api.id, prov, lbl, key, usgs, mv, mg, vl)}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  ) : (
                    <div className="p-4 flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold truncate">{api.label}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                            {PROVIDERS.find(p => p.id === api.provider)?.label}
                          </Badge>
                          {api.usages.map(u => (
                            <Badge key={u} variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                              {u === 'generation' ? 'Generation' : 'Vision'}
                            </Badge>
                          ))}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">{api.key_hint}</span>
                        {api.model_vision && <span className="text-xs text-muted-foreground/70 font-mono">V: {api.model_vision}</span>}
                        {api.model_generation && <span className="text-xs text-muted-foreground/70 font-mono">G: {api.model_generation}</span>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Toggle on={api.is_active} onChange={v => toggleApi(api.id, v)} />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => setEditing(api.id)} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => removeApi(api.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Remove</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
              {apis.length === 0 && editing !== 'new' && (
                <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
                  No API keys yet — add one to get started
                </div>
              )}
            </div>
          </section>
        )}

        {/* Cloud Storage */}
        {activeSection === 'cloud' && (
          <section className="flex flex-col gap-4">
            <div>
              <h2 className="text-base font-semibold tracking-tight">Cloud & Storage</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Connect accounts for file input &amp; output</p>
            </div>
            <div className="flex flex-col gap-3">
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                      <path d="M4.5 19L8.5 12L4 5H12L16 12L12 19H4.5Z" fill="#0F9D58"/>
                      <path d="M16 12L20 19H8L12 12H16Z" fill="#4285F4"/>
                      <path d="M8.5 12L4 5H16L20 12H8.5Z" fill="#FFBB00"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">Google Drive</div>
                    <div className="text-xs text-muted-foreground">{cloud.google.connected ? cloud.google.email : 'Not connected'}</div>
                  </div>
                  {cloud.google.connected
                    ? <Button size="sm" variant="outline" onClick={disconnectGoogle}>Disconnect</Button>
                    : <Button size="sm" onClick={connectGoogle}>Connect</Button>}
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0061FF">
                      <path d="M6 2L1 5.5l5 3.5 5-3.5L6 2zM16 2l-5 3.5 5 3.5 5-3.5L16 2zM1 12.5L6 16l5-3.5L6 9l-5 3.5zM16 9l-5 3.5 5 3.5 5-3.5L16 9zM6 17.5l5 3.5 5-3.5-5-3.5-5 3.5z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">Dropbox</div>
                    <div className="text-xs text-muted-foreground">{cloud.dropbox.connected ? cloud.dropbox.email : 'Not connected'}</div>
                  </div>
                  {cloud.dropbox.connected
                    ? <Button size="sm" variant="outline" onClick={disconnectDropbox}>Disconnect</Button>
                    : <Button size="sm" onClick={connectDropbox}>Connect</Button>}
                </div>
              </Card>
              {cloudConnectError && (
                <div className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">
                  {cloudConnectError}
                </div>
              )}
              {(!cloud.google.connected || !cloud.dropbox.connected) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 px-1 cursor-default w-fit">
                      <Link className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground underline underline-offset-2 decoration-dashed">Public link also supported</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Paste a public Drive or Dropbox folder URL directly in New Batch</TooltipContent>
                </Tooltip>
              )}
              {(() => {
                const canTransfer = cloud.google.connected || cloud.dropbox.connected
                return (
                  <Card className={`p-4 transition-opacity ${canTransfer ? '' : 'opacity-50'}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold">Auto transfer from Cloud</span>
                        <span className="text-xs text-muted-foreground">
                          {canTransfer
                            ? 'Automatically pull files from input folder (Drive / Dropbox)'
                            : 'Connect Drive or Dropbox to enable'}
                        </span>
                      </div>
                      <Toggle on={canTransfer && autoTransfer} onChange={v => {
                        if (!canTransfer) return
                        setAutoTransfer(v)
                        settingsApi.update({ cloud_auto_transfer: v }).catch(() => setAutoTransfer(!v))
                      }} />
                    </div>
                  </Card>
                )
              })()}
            </div>
            <Card className="p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HardDrive className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-semibold">
                      {storage
                        ? `${(storage.used_gb + storage.system_reserved_gb).toFixed(2)} GB / ${storage.quota_gb} GB`
                        : '— / —'}
                    </div>
                    <div className="text-xs text-muted-foreground">Storage used</div>
                  </div>
                </div>
                {storage && (
                  <Badge
                    variant={storage.percent > 85 ? 'destructive' : storage.percent > 65 ? 'outline' : 'secondary'}
                    className={storage.percent > 65 && storage.percent <= 85 ? 'text-yellow-600 border-yellow-600/30' : ''}
                  >
                    {storage.percent > 85 ? 'Critical' : storage.percent > 65 ? 'High' : 'OK'}
                  </Badge>
                )}
              </div>

              {storage && <StorageBar usedGb={storage.used_gb} reservedGb={storage.system_reserved_gb} quotaGb={storage.quota_gb} />}

              {storage && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="flex flex-col gap-0.5 px-3 py-2.5 rounded-lg bg-secondary/60">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">System / Temp</span>
                    <span className="text-sm font-semibold tabular-nums">{storage.system_reserved_gb.toFixed(2)} GB</span>
                    <span className="text-[10px] text-muted-foreground">10% of total</span>
                  </div>
                  <div className="flex flex-col gap-0.5 px-3 py-2.5 rounded-lg bg-secondary/60">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Free space</span>
                    <span className="text-sm font-semibold tabular-nums">{storage.free_gb.toFixed(2)} GB</span>
                    <span className="text-[10px] text-muted-foreground">available</span>
                  </div>
                  <div className="flex flex-col gap-0.5 px-3 py-2.5 rounded-lg bg-secondary/60">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Image size</span>
                    <span className="text-sm font-semibold tabular-nums">{storage.images_gb.toFixed(2)} GB</span>
                    <span className="text-[10px] text-muted-foreground">{storage.image_count.toLocaleString('en')} files</span>
                  </div>
                  <div className="flex flex-col gap-0.5 px-3 py-2.5 rounded-lg bg-secondary/60">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Generated</span>
                    <span className="text-sm font-semibold tabular-nums">{storage.generated_gb.toFixed(2)} GB</span>
                    <span className="text-[10px] text-muted-foreground">{storage.generated_count.toLocaleString('en')} files</span>
                  </div>
                </div>
              )}
            </Card>
          </section>
        )}

        {/* Input & Output */}
        {activeSection === 'output' && (
          <section className="flex flex-col gap-6">
            <div>
              <h2 className="text-base font-semibold tracking-tight">Input & Output</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Naming conventions for input and output files</p>
            </div>

            {/* Input naming — hidden (vision is used directly, no manual naming config needed) */}
            <Card className="hidden p-4 flex flex-col gap-4">
              <div>
                <div className="text-sm font-semibold">Input naming</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  At upload, Gemini Vision extracts text from each image and matches it against the catalogue
                </div>
              </div>
              {!hasVisionModule ? (
                <p className="text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 bg-secondary/40">
                  Gemini Vision module not enabled — contact your administrator to activate it.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={!hasCatalogue || apis.length === 0}
                      onClick={() => setGeminiRename(true)}
                      className={[
                        "px-4 h-8 rounded-lg border text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                        geminiRename ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                      ].join(' ')}
                    >
                      Enabled
                    </button>
                    <button
                      onClick={() => setGeminiRename(false)}
                      className={[
                        "px-4 h-8 rounded-lg border text-xs font-medium transition-colors",
                        !geminiRename ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                      ].join(' ')}
                    >
                      Disabled
                    </button>
                    {apis.length === 0 && (
                      <span className="text-xs text-muted-foreground">
                        — <button onClick={() => setActiveSection('api')} className="underline underline-offset-2 hover:text-foreground transition-colors">Add an API key</button> to enable
                      </span>
                    )}
                  </div>
                  {!hasCatalogue && apis.length > 0 && (
                    <p className="text-xs text-muted-foreground">Requires at least one imported catalogue.</p>
                  )}
                </>
              )}
              {!geminiRename && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 bg-secondary/40 font-mono">
                  DSC00271_Model1.{outputFormat}
                  <span className="font-sans text-muted-foreground/60 ml-1">— original name + model, cannot map to catalogue</span>
                </div>
              )}
            </Card>

            {/* Output naming — hidden (vision is used directly, no manual naming config needed) */}
            <Card className="hidden p-4 flex flex-col gap-4">
              <div>
                <div className="text-sm font-semibold">Output naming</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {!hasVisionModule
                    ? 'Gemini Vision module not enabled'
                    : geminiRename
                      ? 'Filename convention for generated images'
                      : 'Gemini Vision disabled — output will use original filename + model suffix'}
                </div>
              </div>

              {!geminiRename && (
                <div className="flex flex-col gap-1.5">
                  <div className="text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 bg-secondary/40 font-mono">
                    DSC00271_Model1.{outputFormat}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{hasVisionModule ? 'Enable Gemini Vision above to customize output naming from catalogue fields.' : 'Contact your administrator to enable Gemini Vision.'}</p>
                </div>
              )}

              {geminiRename && (
                <>
                  <div className="flex flex-wrap gap-2">
                    {([
                      { id: 'ref_modele', label: 'Model ref',    example: 'RB3025'        },
                      { id: 'marque_ref', label: 'Brand + Ref',  example: 'RAYBAN_RB3025' },
                      { id: 'marque_inv', label: 'Brand + N° inv', example: 'RAYBAN_42'   },
                      { id: 'custom',     label: 'Custom',       example: null            },
                    ] as const).map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setOutputNaming(opt.id)}
                        disabled={!hasCatalogue && opt.id !== 'custom'}
                        className={[
                          "flex flex-col items-start px-3 py-2 rounded-lg border text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                          outputNaming === opt.id ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                        ].join(' ')}
                      >
                        <span className="font-medium">{opt.label}</span>
                        {opt.example && <span className="font-mono opacity-60 mt-0.5">{opt.example}</span>}
                      </button>
                    ))}
                  </div>

                  {outputNaming === 'custom' && (
                    <div className="flex flex-col gap-2">
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-1.5">
                        Available variables:
                        {['{ref_modele}', '{marque}', '{n_inventaire}', '{sexe}'].map(v => (
                          <code key={v}
                            className="font-mono bg-secondary px-1.5 py-0.5 rounded cursor-pointer hover:bg-foreground hover:text-background transition-colors"
                            onClick={() => setNamingCustom(p => p + v)}
                          >{v}</code>
                        ))}
                      </div>
                      <input
                        value={namingCustom}
                        onChange={e => setNamingCustom(e.target.value)}
                        placeholder="{ref_modele}"
                        className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
                      />
                      <div className="text-[11px] text-muted-foreground">
                        Preview: <span className="font-mono">
                          {namingCustom
                            .replace('{ref_modele}', 'RB3025')
                            .replace('{marque}', 'RAYBAN')
                            .replace('{n_inventaire}', '42')
                            .replace('{sexe}', 'H')
                          }.{outputFormat}
                        </span>
                      </div>
                    </div>
                  )}

                  {outputNaming !== 'custom' && hasCatalogue && (
                    <div className="text-[11px] text-muted-foreground">
                      Preview: <span className="font-mono">
                        {outputNaming === 'ref_modele' ? 'RB3025' :
                         outputNaming === 'marque_ref' ? 'RAYBAN_RB3025' :
                         'RAYBAN_42'}.{outputFormat}
                      </span>
                    </div>
                  )}
                </>
              )}
            </Card>

            {/* Output Format */}
            <Card className="p-4 flex flex-col gap-3">
              <div className="text-sm font-semibold">Output format</div>
              <div className="flex gap-2">
                {(['jpeg', 'png', 'webp'] as const).map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => { setOutputFormat(fmt); saveProps({ outputFormat: fmt }) }}
                    className={[
                      "px-4 h-8 rounded-lg border text-xs font-mono font-medium uppercase transition-colors",
                      outputFormat === fmt ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                    ].join(' ')}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
            </Card>

            {/* Output Resolution */}
            <Card className="p-4 flex flex-col gap-3">
              <div className="text-sm font-semibold">Output resolution</div>
              <div className="flex gap-2">
                {([
                  { value: '1080x1920', label: '1K' },
                  { value: '1440x2560', label: '2K' },
                  { value: '2160x3840', label: '4K' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => { setOutputResolution(value); saveProps({ outputResolution: value }) }}
                    className={[
                      "px-4 h-8 rounded-lg border text-xs font-mono font-medium transition-colors",
                      outputResolution === value ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Default for new jobs — each job can override it.</p>
            </Card>

            {/* AI Input Size */}
            <Card className="p-4 flex flex-col gap-3">
              <div className="text-sm font-semibold">AI input size</div>
              <div className="flex gap-2">
                {([
                  { value: 512,  label: '512px'  },
                  { value: 1024, label: '1024px' },
                  { value: 2048, label: '2048px' },
                  { value: 0,    label: 'Original' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => { setMaxInputPx(value); saveProps({ maxInputPx: value }) }}
                    className={[
                      "px-4 h-8 rounded-lg border text-xs font-mono font-medium transition-colors",
                      (maxInputPx ?? 1024) === value ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Product photos are downscaled to this size before being sent to the AI.
                Larger inputs and outputs cost more API tokens — billed to your key.
              </p>
            </Card>
          </section>
        )}

        {/* Custom Data */}
        {activeSection === 'sku' && (
          <div className="flex flex-col gap-8">
            {hasCatalogue && (
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm text-blue-700 dark:text-blue-300">
                <span className="text-base shrink-0">ℹ</span>
                <div>
                  <span className="font-semibold">Catalogue actif</span>
                  <span className="block text-xs mt-0.5 opacity-70">Les données du catalogue (Marque, Ref, Sexe…) sont utilisées automatiquement — les règles manuelles ci-dessous sont ignorées.</span>
                </div>
              </div>
            )}
            <div className={hasCatalogue ? "opacity-40 pointer-events-none select-none" : ""}>
              <SkuRulesSection rules={skuRules} onChange={onSkuRulesChange} avatars={avatars} />
              <div className="border-t border-border my-8" />
              <CategoryRulesSection rules={categoryRules} onChange={setCategoryRules} />
              <div className="border-t border-border my-8" />
              <TypeRulesSection rules={typeRules} onChange={setTypeRules} />
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
